import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Button,
  Box,
  Typography,
  IconButton,
  InputAdornment,
  Link,
  Grow,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { Visibility, VisibilityOff, Close } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { showAlert } from '../../components/service/AlertService';
import { forgotPassword as apiForgotPassword } from '../../services/api/auth';

const Transition = React.forwardRef((props, ref) => {
  return <Grow ref={ref} {...props} />;
});

const AuthModal = ({ open, onClose }) => {
  const { t, i18n } = useTranslation();
  const { login, register } = useAuth();
  const { actualTheme } = useTheme();
  const [isLogin, setIsLogin] = useState(true);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    invitationCode: '',
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    birthDate: '',
    termsAccepted: false,
    language: i18n.language,
  });

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleCheckboxChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.checked,
    });
  };

  const handleForgotPasswordClick = useCallback((e) => {
    e.preventDefault();
    setIsForgotPassword(true);
    setFormData({
      invitationCode: '',
      username: '',
      email: '',
      password: '',
      confirmPassword: '',
      birthDate: '',
      termsAccepted: false,
      language: i18n.language,
    });
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Login
    if (isLogin && !isForgotPassword) {
      if (!formData.username || !formData.password) {
        showAlert("LOGIN_FIELDS_REQUIRED");
        return;
      }
      try {
        const result = await login({
          username: formData.username,
          password: formData.password,
        });
        if (result.success) {
          onClose();
        } else {
          showAlert(result.error);
        }
      } catch (err) {
        showAlert("ERROR_LOGIN");
      }
      return;
    }

    // Forgot password
    if (isForgotPassword) {
      if (!formData.email) {
        showAlert("PASSWORD_RESET_EMAIL_REQUIRED");
        return;
      }
      try {
        const result = await apiForgotPassword(formData.email, formData.language);
        if (result.data) {
          showAlert("PASSWORD_RESET_EMAIL_SENT");
          setIsForgotPassword(false);
          setFormData({
            invitationCode: '',
            username: '',
            email: '',
            password: '',
            confirmPassword: '',
            birthDate: '',
            termsAccepted: false,
            language: i18n.language,
          });
        } else {
          showAlert(result.error.code || "ERROR_FORGOT_PASSWORD");
        }
      } catch (err) {
        showAlert("ERROR_FORGOT_PASSWORD");
      }
      return;
    }

    // Register
    if (!isLogin) {
      if (formData.password !== formData.confirmPassword) {
        showAlert("PASSWORD_DONT_MATCH");
        return;
      }
      if (!formData.termsAccepted) {
        showAlert("MUST_ACCEPT_TERMS");
        return;
      }
      if (!formData.birthDate) {
        showAlert("BIRTH_DATE_REQUIRED");
        return;
      }
      const birth = new Date(formData.birthDate);
      const age = Math.floor((new Date() - birth) / (365.25 * 24 * 60 * 60 * 1000));
      if (age < 18) {
        showAlert("BIRTH_DATE_MUST_BE_OVER_18");
        return;
      }
      try {
        const result = await register({
          username: formData.username,
          email: formData.email,
          password: formData.password,
          invitationCode: formData.invitationCode,
          birthDate: formData.birthDate,
          termsAccepted: formData.termsAccepted,
          language: formData.language,
        });
        if (result.success) {
          onClose();
        } else {
          showAlert(result.error);
        }
      } catch (err) {
        showAlert("ERROR_LOGIN");
      }
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      TransitionComponent={Transition}
      TransitionProps={{
        timeout: {
          enter: 400,
          exit: 300,
        },
      }}
      PaperProps={{
        sx: {
          background: (theme) =>
            theme.palette.mode === 'light'
              ? 'rgba(255, 255, 255, 0.9)'
              : 'rgba(24, 24, 28, 0.98)',
          backdropFilter: 'blur(10px)',
          border: (theme) =>
            theme.palette.mode === 'light'
              ? '1px solid rgba(0, 0, 0, 0.1)'
              : '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
          transition: 'all 0.3s ease-in-out',
        },
      }}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            backdropFilter: 'blur(4px)',
            transition: 'opacity 0.3s ease-in-out',
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          position: 'relative',
          pb: 3,
          pt: 4,
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
          background: (theme) =>
            theme.palette.mode === 'light'
              ? 'linear-gradient(180deg, rgba(33, 150, 243, 0.1) 0%, rgba(33, 150, 243, 0) 100%)'
              : 'linear-gradient(180deg, rgba(33, 150, 243, 0.1) 0%, rgba(33, 150, 243, 0) 100%)',
        }}
      >
        <Typography
          variant="h4"
          sx={{
            fontWeight: 300,
            letterSpacing: '0.15em',
            color: 'text.primary',
            position: 'absolute',
            left: '50%',
            transform: 'translateX(-50%)',
            width: '100%',
            textAlign: 'center',
            fontSize: '1.5rem',
          }}
        >
          {isForgotPassword
            ? t('auth.forgotPassword.title').toUpperCase()
            : isLogin
            ? t('auth.login.title').toUpperCase()
            : t('auth.register.title').toUpperCase()}
        </Typography>
        <IconButton
          onClick={onClose}
          sx={{
            color: 'text.secondary',
            position: 'absolute',
            right: 8,
            top: 8,
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              transform: 'scale(1.1)',
              color: 'primary.main',
            },
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
          {isForgotPassword ? (
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label={t('auth.forgotPassword.email')}
              name="email"
              autoComplete="email"
              autoFocus
              value={formData.email}
              onChange={handleInputChange}
              sx={{
                '& .MuiOutlinedInput-root': {
                  background: (theme) =>
                    theme.palette.mode === 'light'
                      ? 'rgba(0, 0, 0, 0.05)'
                      : 'rgba(255, 255, 255, 0.05)',
                  '& fieldset': {
                    borderColor: (theme) => theme.palette.divider,
                  },
                  '&:hover fieldset': {
                    borderColor: (theme) =>
                      theme.palette.mode === 'light'
                        ? 'rgba(0, 0, 0, 0.3)'
                        : 'rgba(255, 255, 255, 0.2)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'primary.main',
                  },
                },
                '& .MuiInputLabel-root': {
                  color: 'text.secondary',
                  '&.Mui-focused': {
                    color: 'primary.main',
                  },
                },
                '& .MuiOutlinedInput-input': {
                  color: 'text.primary',
                },
              }}
            />
          ) : isLogin ? (
            <>
              <TextField
                margin="normal"
                required
                fullWidth
                id="username-login"
                label={t('auth.login.username')}
                name="username"
                autoComplete="username"
                autoFocus
                value={formData.username}
                onChange={handleInputChange}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    background: (theme) =>
                      theme.palette.mode === 'light'
                        ? 'rgba(0, 0, 0, 0.05)'
                        : 'rgba(255, 255, 255, 0.05)',
                    '& fieldset': {
                      borderColor: (theme) => theme.palette.divider,
                    },
                    '&:hover fieldset': {
                      borderColor: (theme) =>
                        theme.palette.mode === 'light'
                          ? 'rgba(0, 0, 0, 0.3)'
                          : 'rgba(255, 255, 255, 0.2)',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'primary.main',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: 'text.secondary',
                    '&.Mui-focused': {
                      color: 'primary.main',
                    },
                  },
                  '& .MuiOutlinedInput-input': {
                    color: 'text.primary',
                  },
                }}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label={t('auth.login.password')}
                type={showPassword ? 'text' : 'password'}
                id="password"
                autoComplete="current-password"
                value={formData.password}
                onChange={handleInputChange}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        sx={{ color: 'text.secondary' }}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    background: (theme) =>
                      theme.palette.mode === 'light'
                        ? 'rgba(0, 0, 0, 0.05)'
                        : 'rgba(255, 255, 255, 0.05)',
                    '& fieldset': {
                      borderColor: (theme) => theme.palette.divider,
                    },
                    '&:hover fieldset': {
                      borderColor: (theme) =>
                        theme.palette.mode === 'light'
                          ? 'rgba(0, 0, 0, 0.3)'
                          : 'rgba(255, 255, 255, 0.2)',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'primary.main',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: 'text.secondary',
                    '&.Mui-focused': {
                      color: 'primary.main',
                    },
                  },
                  '& .MuiOutlinedInput-input': {
                    color: 'text.primary',
                  },
                }}
              />
              <Box sx={{ textAlign: 'right', mt: 1 }}>
                <Link
                  component="button"
                  type="button"               // <-- evita submit al pulsar Enter
                  variant="body2"
                  onClick={handleForgotPasswordClick}
                  sx={{
                    color: 'primary.main',
                    textDecoration: 'none',
                    fontWeight: 500,
                    '&:hover': {
                      textDecoration: 'underline',
                    },
                  }}
                >
                  {t('auth.login.forgotPassword')}
                </Link>
              </Box>
            </>
          ) : (
            <>
              <TextField
                margin="normal"
                required
                fullWidth
                id="invitationCode"
                label={t('auth.register.invitationCode')}
                name="invitationCode"
                autoFocus
                value={formData.invitationCode}
                onChange={handleInputChange}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    background: (theme) =>
                      theme.palette.mode === 'light'
                        ? 'rgba(0, 0, 0, 0.05)'
                        : 'rgba(255, 255, 255, 0.05)',
                    '& fieldset': {
                      borderColor: (theme) => theme.palette.divider,
                    },
                    '&:hover fieldset': {
                      borderColor: (theme) =>
                        theme.palette.mode === 'light'
                          ? 'rgba(0, 0, 0, 0.3)'
                          : 'rgba(255, 255, 255, 0.2)',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'primary.main',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: 'text.secondary',
                    '&.Mui-focused': {
                      color: 'primary.main',
                    },
                  },
                  '& .MuiOutlinedInput-input': {
                    color: 'text.primary',
                  },
                }}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                id="username-register"
                label={t('auth.register.username')}
                name="username"
                autoComplete="username"
                value={formData.username}
                onChange={handleInputChange}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    background: (theme) =>
                      theme.palette.mode === 'light'
                        ? 'rgba(0, 0, 0, 0.05)'
                        : 'rgba(255, 255, 255, 0.05)',
                    '& fieldset': {
                      borderColor: (theme) => theme.palette.divider,
                    },
                    '&:hover fieldset': {
                      borderColor: (theme) =>
                        theme.palette.mode === 'light'
                          ? 'rgba(0, 0, 0, 0.3)'
                          : 'rgba(255, 255, 255, 0.2)',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'primary.main',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: 'text.secondary',
                    '&.Mui-focused': {
                      color: 'primary.main',
                    },
                  },
                  '& .MuiOutlinedInput-input': {
                    color: 'text.primary',
                  },
                }}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label={t('auth.register.email')}
                name="email"
                autoComplete="email"
                value={formData.email}
                onChange={handleInputChange}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    background: (theme) =>
                      theme.palette.mode === 'light'
                        ? 'rgba(0, 0, 0, 0.05)'
                        : 'rgba(255, 255, 255, 0.05)',
                    '& fieldset': {
                      borderColor: (theme) => theme.palette.divider,
                    },
                    '&:hover fieldset': {
                      borderColor: (theme) =>
                        theme.palette.mode === 'light'
                          ? 'rgba(0, 0, 0, 0.3)'
                          : 'rgba(255, 255, 255, 0.2)',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'primary.main',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: 'text.secondary',
                    '&.Mui-focused': {
                      color: 'primary.main',
                    },
                  },
                  '& .MuiOutlinedInput-input': {
                    color: 'text.primary',
                  },
                }}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label={t('auth.login.password')}
                type={showPassword ? 'text' : 'password'}
                id="password"
                autoComplete="new-password"
                value={formData.password}
                onChange={handleInputChange}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        sx={{ color: 'text.secondary' }}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    background: (theme) =>
                      theme.palette.mode === 'light'
                        ? 'rgba(0, 0, 0, 0.05)'
                        : 'rgba(255, 255, 255, 0.05)',
                    '& fieldset': {
                      borderColor: (theme) => theme.palette.divider,
                    },
                    '&:hover fieldset': {
                      borderColor: (theme) =>
                        theme.palette.mode === 'light'
                          ? 'rgba(0, 0, 0, 0.3)'
                          : 'rgba(255, 255, 255, 0.2)',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'primary.main',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: 'text.secondary',
                    '&.Mui-focused': {
                      color: 'primary.main',
                    },
                  },
                  '& .MuiOutlinedInput-input': {
                    color: 'text.primary',
                  },
                }}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                name="confirmPassword"
                label={t('auth.register.confirmPassword')}
                type={showPassword ? 'text' : 'password'}
                id="confirmPassword"
                autoComplete="new-password"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        sx={{ color: 'text.secondary' }}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    background: (theme) =>
                      theme.palette.mode === 'light'
                        ? 'rgba(0, 0, 0, 0.05)'
                        : 'rgba(255, 255, 255, 0.05)',
                    '& fieldset': {
                      borderColor: (theme) => theme.palette.divider,
                    },
                    '&:hover fieldset': {
                      borderColor: (theme) =>
                        theme.palette.mode === 'light'
                          ? 'rgba(0, 0, 0, 0.3)'
                          : 'rgba(255, 255, 255, 0.2)',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'primary.main',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: 'text.secondary',
                    '&.Mui-focused': {
                      color: 'primary.main',
                    },
                  },
                  '& .MuiOutlinedInput-input': {
                    color: 'text.primary',
                  },
                }}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                id="birthDate"
                label={t('auth.register.birthDate')}
                name="birthDate"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={formData.birthDate}
                onChange={handleInputChange}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    background: (theme) =>
                      theme.palette.mode === 'light'
                        ? 'rgba(0, 0, 0, 0.05)'
                        : 'rgba(255, 255, 255, 0.05)',
                    '& fieldset': {
                      borderColor: (theme) => theme.palette.divider,
                    },
                    '&:hover fieldset': {
                      borderColor: (theme) =>
                        theme.palette.mode === 'light'
                          ? 'rgba(0, 0, 0, 0.3)'
                          : 'rgba(255, 255, 255, 0.2)',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'primary.main',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: 'text.secondary',
                    '&.Mui-focused': {
                      color: 'primary.main',
                    },
                  },
                  '& .MuiOutlinedInput-input': {
                    color: 'text.primary',
                  },
                }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.termsAccepted}
                    onChange={handleCheckboxChange}
                    name="termsAccepted"
                    color="primary"
                  />
                }
                label={
                  <Typography variant="body2" color="text.secondary">
                    {t('auth.register.acceptTerms')}{' '}
                    <Link href="/terms-and-conditions" target="_blank" sx={{ color: 'primary.main' }}>
                      {t('auth.register.termsLink')}
                    </Link>{' '}
                    {t('auth.register.and')}{' '}
                    <Link href="/privacy-policy" target="_blank" sx={{ color: 'primary.main' }}>
                      {t('auth.register.privacyLink')}
                    </Link>
                  </Typography>
                }
                sx={{ mt: 1, mb: 1 }}
              />
            </>
          )}
          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            sx={{
              mt: 3,
              mb: 2,
              textTransform: 'none',
              fontWeight: 400,
              background: (theme) =>
                theme.palette.mode === 'light'
                  ? 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)'
                  : 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
              boxShadow: '0 3px 5px 2px rgba(33, 203, 243, 0.3)',
              '&:hover': {
                background: (theme) =>
                  theme.palette.mode === 'light'
                    ? 'linear-gradient(45deg, #1976D2 30%, #00B4E5 90%)'
                    : 'linear-gradient(45deg, #1976D2 30%, #00B4E5 90%)',
              },
            }}
          >
            {isForgotPassword
              ? t('auth.forgotPassword.submit')
              : isLogin
              ? t('auth.login.submit')
              : t('auth.register.submit')}
          </Button>
          <Box
            sx={{
              mt: 2,
              textAlign: 'center',
              p: 2,
              background: (theme) =>
                theme.palette.mode === 'light'
                  ? 'rgba(33, 150, 243, 0.1)'
                  : 'linear-gradient(45deg, rgba(33, 150, 243, 0.1) 30%, rgba(33, 203, 243, 0.1) 90%)',
              borderRadius: 1,
              border: (theme) => `1px solid ${theme.palette.divider}`,
            }}
          >
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                display: 'inline',
                mr: 1,
                fontWeight: 300,
              }}
            >
              {isForgotPassword
                ? t('auth.forgotPassword.backToLogin')
                : isLogin
                ? t('auth.login.noAccount')
                : t('auth.register.alreadyHaveAccount')}
            </Typography>
            <Link
              component="button"
              type="button"               // <-- evita submit al pulsar Enter
              variant="body2"
              onClick={() => {
                if (isForgotPassword) {
                  setIsForgotPassword(false);
                  setIsLogin(true);
                } else {
                  setIsLogin(!isLogin);
                }
                setFormData({
                  invitationCode: '',
                  username: '',
                  email: '',
                  password: '',
                  confirmPassword: '',
                  birthDate: '',
                  termsAccepted: false,
                  language: i18n.language,
                });
              }}
              sx={{
                color: 'primary.main',
                textDecoration: 'none',
                fontWeight: 500,
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
            >
              {isForgotPassword
                ? t('auth.forgotPassword.signInHere')
                : isLogin
                ? t('auth.login.registerHere')
                : t('auth.register.signInHere')}
            </Link>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default AuthModal;